import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Row, Space, Spin } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuestionSummary } from '../../../api/CodeFrames';
import { useCodeFrames } from '../../../api/Questions';
import { useCreatePersistedSegments, usePersistedSegments } from '../../../api/Segments';
import Loading from '../../../components/Loading';
import {
  ChartValueType,
  QCPhase,
  QueryKeys,
  SegmentType,
  SurveyUploadType,
  UserRoleType,
} from '../../../constants';
import useParams from '../../../hooks/useParams';
import useRoles from '../../../hooks/useRoles';
import { queryType, surveyType } from '../../../types';
import ManageThemesModal from '../ManageThemesModal';
import OpenEndedQuestionsSelect from '../OpenEndedQuestionsSelect';
import ChartValueTypeToggle from './ChartValueTypeToggle';
import QuestionSummary from './QuestionSummary';
import RawDataExportModal from './RawDataExportModal';
import SegmentThemesCard from './SegmentThemesCard';
import ThemesChart from './ThemesChart';

function ThemesTab({
  survey,
  questions,
  questionResponseOptions,
  selectedQuestionId = undefined,
  setSelectedQuestionId,
  selectedSegmentQuestionId = undefined,
  setSelectedSegmentQuestionId,
  statAssistEnabled,
  setStatAssistEnabled,
  highlightThemeIds = [],
  setActiveSegmentTypes,
}) {
  const { surveyId } = useParams();
  const queryClient = useQueryClient();
  const { roles } = useRoles();

  const [isThemesModalVisible, setIsThemesModalVisible] = useState(false);
  const [chartValueType, setChartValueType] = useState(
    survey.upload_type === SurveyUploadType.SURVEY
      ? ChartValueType.PERCENTAGE
      : ChartValueType.COUNT,
  );
  const [hiddenThemes, setHiddenThemes] = useState([]);
  const [isExportModalVisible, setExportModalVisible] = useState(false);

  const openEndedQuestions = questions.data.filter((q) => q.has_themes);
  const question = questions.data.find((q) => q.id === selectedQuestionId);

  const { mutate: createPersistedSegments, isLoading: createPersistedSegmentsLoading } =
    useCreatePersistedSegments({
      onSettled: async ({ data }, _error, variables) => {
        await queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], (prev) => {
          if (variables.data.some((s) => s.segment_type === SegmentType.ATTRIBUTE)) {
            const allRespondentSegment = prev.data.find(
              (s) => s.segment_type === SegmentType.ALL_RESPONDENTS,
            );
            const customSegments = prev.data.filter((s) => s.segment_type === SegmentType.CUSTOM);
            return {
              ...prev,
              data: [{ ...allRespondentSegment, active: false }, ...customSegments, ...data],
            };
          }
          return { ...prev, data: [...prev.data, ...data] };
        });
      },
    });

  const { data: summary, isFetching: summaryFetching } = useQuestionSummary(
    { surveyId, codeFrameId: question.code_frame_id },
    {
      enabled: !!selectedQuestionId && question.qc_phase === QCPhase.COMPLETE,
      keepPreviousData: true,
    },
  );

  const { data: persistedSegments, isLoading: persistedSegmentsLoading } = usePersistedSegments(
    { surveyId },
    {
      select: (data) => {
        const sortSegments = (a, b) => {
          // Sort by ID if the segment types are the same
          if (a.segment_type === b.segment_type) {
            return a.id - b.id;
          }

          // Define custom sorting order
          const order = {
            [SegmentType.ALL_RESPONDENTS]: -2,
            [SegmentType.ATTRIBUTE]: -1,
            [SegmentType.CUSTOM]: 1,
          };

          // Compare based on segment type
          return order[a.segment_type] - order[b.segment_type];
        };
        return data.data.sort(sortSegments);
      },
      onSuccess: async (data) => {
        // if there aren't any segments create the all respondents segment
        if (!data.length) {
          createPersistedSegments({
            surveyId,
            data: [
              {
                active: true,
                segment: undefined,
                segment_type: SegmentType.ALL_RESPONDENTS,
                label: 'All Respondents',
              },
            ],
          });
        }

        // if there is an attribute segment, set the selected segment question to the attribute question
        if (data.some((s) => s.segment_type === SegmentType.ATTRIBUTE)) {
          setSelectedSegmentQuestionId(
            parseInt(
              Object.keys(data.find((s) => s.segment_type === SegmentType.ATTRIBUTE).segment)[0],
              10,
            ),
          );
        }
      },
    },
  );

  const segmentThemes = useCodeFrames(
    persistedSegments?.map((segment) => {
      const params = new URLSearchParams();
      if (segment.segment) {
        Object.keys(segment.segment).forEach((key) => {
          if (segment.segment[key]?.length) {
            params.append(key, segment.segment[key].join('_$SEPARATOR$_'));
          }
        });
      }
      return {
        questionId: selectedQuestionId,
        filters: params.toString(),
        surveyId,
        hiddenThemes,
        select: (data) => {
          if (!data.data) {
            return { themes: [], response_count: 0, segmentId: segment.id };
          }
          return {
            ...data.data.themes_data,
            segmentId: segment.id,
          };
        },
      };
    }),
    {
      enabled: !!selectedQuestionId,
      cacheTime: 0,
    },
  );

  useEffect(() => {
    if (persistedSegments) {
      setActiveSegmentTypes(persistedSegments.filter((s) => s.active).map((s) => s.segment_type));
    }
  }, [persistedSegments, setActiveSegmentTypes]);

  return (
    <>
      <OpenEndedQuestionsSelect
        survey={survey}
        openEndedQuestions={openEndedQuestions}
        selectedQuestionId={selectedQuestionId}
        setSelectedQuestionId={setSelectedQuestionId}
        extra={
          !roles.includes(UserRoleType.CLIENT) && [
            <Button key="export" onClick={() => setExportModalVisible(true)} size="small">
              Export raw data
            </Button>,
          ]
        }
      />
      {summary?.data && (
        <QuestionSummary
          question={question}
          summary={summary.data}
          summaryFetching={summaryFetching}
        />
      )}
      {persistedSegmentsLoading || createPersistedSegmentsLoading ? (
        <Loading />
      ) : (
        <Flex vertical gap={16}>
          {survey.upload_type === SurveyUploadType.SURVEY && (
            <SegmentThemesCard
              segments={persistedSegments}
              questions={questions.data.filter((q) => q.is_filterable)}
              questionResponseOptions={questionResponseOptions}
              selectedQuestion={question}
              selectedSegmentQuestionId={selectedSegmentQuestionId}
              setSelectedSegmentQuestionId={setSelectedSegmentQuestionId}
              statAssistEnabled={statAssistEnabled}
              setStatAssistEnabled={setStatAssistEnabled}
              createPersistedSegments={createPersistedSegments}
            />
          )}
          {segmentThemes.length && !segmentThemes.some((r) => r.isLoading) ? (
            <>
              <ThemesChart
                question={question}
                themes={segmentThemes[0].data?.themes}
                activeSegments={persistedSegments?.filter((s) => s.active)}
                segmentThemes={segmentThemes}
                chartValueType={chartValueType}
                hiddenThemes={hiddenThemes}
                highlightThemeIds={
                  statAssistEnabled &&
                  persistedSegments.every((s) =>
                    s.active ? s.segment_type === SegmentType.ATTRIBUTE : true,
                  )
                    ? highlightThemeIds
                    : []
                }
              />
              <Space>
                <ChartValueTypeToggle
                  key="chartValueType"
                  chartValueType={chartValueType}
                  setChartValueType={setChartValueType}
                />
                <Button
                  key="manageThemes"
                  size="small"
                  onClick={() => {
                    setIsThemesModalVisible(true);
                  }}
                >
                  Manage themes
                </Button>
              </Space>
            </>
          ) : (
            <Row justify="center">
              <Spin size="large" />
            </Row>
          )}
        </Flex>
      )}
      <ManageThemesModal
        themes={segmentThemes[0]?.data?.themes}
        saveThemes={setHiddenThemes}
        hiddenThemes={hiddenThemes}
        isModalVisible={isThemesModalVisible}
        setIsModalVisible={setIsThemesModalVisible}
        codeFrameId={question.code_frame_id}
        surveyId={surveyId}
      />
      <RawDataExportModal
        visible={isExportModalVisible}
        closeModal={() => setExportModalVisible(false)}
        openEndedQuestions={openEndedQuestions.filter((q) => q.qc_phase === QCPhase.COMPLETE)}
        selectedQuestionId={selectedQuestionId}
        surveyId={surveyId}
      />
    </>
  );
}

ThemesTab.propTypes = {
  survey: surveyType.isRequired,
  questions: queryType.isRequired,
  questionResponseOptions: PropTypes.arrayOf(queryType).isRequired,
  selectedQuestionId: PropTypes.number,
  setSelectedQuestionId: PropTypes.func.isRequired,
  selectedSegmentQuestionId: PropTypes.number,
  setSelectedSegmentQuestionId: PropTypes.func.isRequired,
  statAssistEnabled: PropTypes.bool.isRequired,
  setStatAssistEnabled: PropTypes.func.isRequired,
  highlightThemeIds: PropTypes.arrayOf(PropTypes.number),
  setActiveSegmentTypes: PropTypes.func.isRequired,
};

export default ThemesTab;
